import React, { useMemo, useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UserType } from '../../graphql/generated/graphql';
import type { EntitySelectConfig } from '../../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../../search/entitySearch/CategorizedEntitySelect';
import type { BaseModalProps } from '../../ui/uiSlice';
import UserRow from '../../user/UserRow';
import { useAllBots, useGetUser } from '../../user/useAllUsers';
import * as styles from './TransferPostOwnershipModal.module.scss';

const TRANSFER_POST_OWNERSHIP_MODAL_USER_FRAGMENT = gql(`
  fragment TransferPostOwnershipModal_user on User {
    ...useGetUser_user
  }
`);

type NewOwner = { id: string; name: string };
interface TransferPostOwnershipModalProps extends BaseModalProps {
  onConfirm: (newOwner: NewOwner) => Promise<void>;
  user: DocumentType<typeof TRANSFER_POST_OWNERSHIP_MODAL_USER_FRAGMENT>;
  deactivating: boolean;
}

const TransferPostOwnershipModal = ({
  close,
  onConfirm,
  user,
  deactivating,
}: TransferPostOwnershipModalProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const [isEditing, setIsEditing] = useState(false);
  const [newOwner, setNewOwner] = useState<NewOwner>(currentUser);
  const bot = useAllBots()[0];

  const getUser = useGetUser();
  const fullUser = getUser(user);

  const config = useMemo<EntitySelectConfig>(
    () => ({
      user: {
        label: 'Pick a new owner',
        filter: (u) => ![newOwner.id, user.id, bot?.id].includes(u.id),
        sortBy: (a, b) => {
          if (a.type === UserType.Admin) {
            return -1;
          }
          if (b.type === UserType.Admin) {
            return 1;
          }
          return 0;
        },
      },
      new: {
        getGroup: bot
          ? () => ({
              label: 'transfer to slab bot',
              options: [
                {
                  id: bot.id,
                  label: bot.name,
                  name: bot.name ?? 'Slab Bot',
                  secondary: bot.email,
                  value: bot.id,
                  avatar: bot.avatar,
                  onSelect: () => {
                    setNewOwner(bot);
                    setIsEditing(false);
                  },
                },
              ],
            })
          : undefined,
      },
      order: ['user', 'new'],
    }),
    [bot, newOwner.id, user.id],
  );

  return (
    <ConfirmationModal
      close={close}
      title={deactivating ? 'Deactivate user?' : 'Transfer post ownership'}
      confirmButton={{
        text: deactivating ? 'Confirm' : 'Transfer',
        onClick: () => onConfirm(newOwner),
      }}
      width={425}
      cancelButtonText="Never mind"
      description={
        deactivating ? (
          <>
            Ownership of {fullUser.name}&apos;s published posts must be
            transferred to another user.
          </>
        ) : (
          <>
            Ownership of {fullUser.name}&apos;s published posts will be
            transferred to a new owner.
          </>
        )
      }
    >
      <h5 className={styles.ownerHeading}>New owner</h5>
      <div className={styles.owner}>
        {isEditing ? (
          <CategorizedEntitySelect
            autoFocus
            isClearable={false}
            onChange={(value) => {
              if (value?.type === 'user' && value.id !== user.id) {
                setNewOwner(value.user);
              }
              setIsEditing(false);
            }}
            value={{ id: newOwner.id, type: 'user' }}
            config={config}
            maxOptionsToShow={6}
            showMoreResults={false}
          />
        ) : (
          <>
            <div className={styles.ownerCard}>
              <UserRow user={getUser(newOwner)} />
            </div>
            <Button
              size="small"
              variant="secondary"
              onClick={() => setIsEditing(true)}
            >
              Change
            </Button>
          </>
        )}
      </div>
    </ConfirmationModal>
  );
};

export default TransferPostOwnershipModal;
